import React from "react";
import { motion } from "framer-motion";
import Section from "./Section";
import { ComingSoonBadge } from "./Badges";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Mic,
  Languages,
  Route,
  LineChart,
  Star,
  Briefcase,
  Coins,
  Wrench,
  Search,
  Eye,
  Cpu,
} from "lucide-react";

const items = [
  { icon: Sparkles, t: "AI Concierge", d: "Chat-to-action, brand-voice ready." },
  { icon: Mic, t: "Voice Assistant", d: "Hands-free requests, staff & guests." },
  { icon: Languages, t: "AI Translation", d: "Context-aware, tone-perfect." },
  { icon: Route, t: "Smart Task Routing", d: "Best-next-action across teams." },
  { icon: LineChart, t: "Predictive Operations", d: "Forecast demand, staff & load." },
  { icon: Star, t: "AI Guest Recommendations", d: "Personalised offers & itineraries." },
  { icon: Briefcase, t: "Executive AI Copilot", d: "Daily GM digests & narratives." },
  { icon: Coins, t: "Revenue Intelligence", d: "Ancillary lift, without becoming an RMS." },
  { icon: Wrench, t: "Predictive Maintenance", d: "IoT + ticket history signals." },
  { icon: Search, t: "Natural Language Search", d: "Ask anything across your ops." },
  { icon: Eye, t: "Computer Vision", d: "Photo checklist verification." },
  { icon: Cpu, t: "Autonomous Operations", d: "The self-running hotel." },
];

export default function FutureVision() {
  return (
    <section
      id="future-vision"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="future-vision-section"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-purpleDeep via-brand-purple to-brand-purpleMid" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-brand-purpleLight/25 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="noise-overlay opacity-60" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-6 inline-flex items-center gap-3 font-display text-[11px] uppercase tracking-[0.28em] text-white/90 font-bold">
          <span className="inline-block w-6 h-px bg-brand-orange" />
          Future vision
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <h2
              className="font-display text-white text-[38px] md:text-[64px] leading-[1] tracking-tight text-balance"
              style={{ fontWeight: 800 }}
            >
              The future of hospitality is{" "}
              <span className="grad-text">intelligent.</span>
            </h2>
            <p className="mt-5 text-white/85 text-[17px] md:text-[19px] leading-relaxed max-w-2xl">
              Today, iNPLASS connects your hotel operations. Tomorrow, it will
              help run them.
            </p>
          </div>
          <div className="md:col-span-4">
            <div className="rounded-xl border border-white/25 bg-white/10 backdrop-blur p-5">
              <div className="font-display text-[10px] uppercase tracking-[0.28em] text-white font-bold">
                Product roadmap
              </div>
              <p className="mt-2 text-white/85 text-[13px] leading-relaxed">
                These capabilities are currently under our product roadmap and
                are not yet part of the live platform.
              </p>
              <Link
                to="/roadmap"
                data-testid="future-vision-roadmap-link"
                className="mt-3 inline-flex items-center gap-2 text-white text-[13px] group font-semibold"
              >
                <span className="border-b border-white/50 group-hover:border-white pb-0.5">
                  See the roadmap
                </span>
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.t}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              className="group relative rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur p-6 overflow-hidden hover:border-white/40 hover:bg-white/[0.10] transition-colors"
              data-testid={`future-item-${it.t.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              <div className="relative flex items-center justify-between">
                <div className="w-10 h-10 rounded-lg bg-white/15 grid place-items-center text-white">
                  <it.icon size={18} />
                </div>
                <ComingSoonBadge />
              </div>
              <div
                className="relative mt-6 font-display text-white text-[19px] leading-tight"
                style={{ fontWeight: 700 }}
              >
                {it.t}
              </div>
              <div className="relative mt-1.5 text-white/75 text-[13px]">
                {it.d}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
